import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  Copy, 
  Check, 
  Mail, 
  Phone, 
  Building2, 
  MessageSquare,
  Users,
  FileText,
  Euro,
  ExternalLink,
  RefreshCw,
  Trash2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateShort = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit'
  });
};

const isNew = (dateStr) => {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  return diff < 24 * 60 * 60 * 1000; // Less than 24 hours
};

const getPreview = (row, keys) => {
  for (const k of keys) {
    if (row && row[k]) return String(row[k]);
  }
  return '';
};

// Copy Button Component
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!text) return;
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        title: 'Kopiert!',
        description: `${label || text} wurde in die Zwischenablage kopiert.`,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Fehler',
        description: 'Kopieren fehlgeschlagen.',
      });
    }
  };

  if (!text) return null;

  return (
    <button
      onClick={handleCopy}
      className="ml-1 p-1 rounded hover:bg-gray-200 transition-colors inline-flex items-center"
      title={`${label || text} kopieren`}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-green-600" />
      ) : (
        <Copy className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600" />
      )}
    </button>
  );
};

// Clickable Email/Phone with Copy
const ContactCell = ({ type, value }) => {
  if (!value) return <span className="text-gray-400">-</span>;

  const href = type === 'email' ? `mailto:${value}` : `tel:${value}`;
  const Icon = type === 'email' ? Mail : Phone;

  return (
    <div className="flex items-center gap-1 group">
      <a 
        href={href} 
        className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
        onClick={(e) => e.stopPropagation()}
      >
        <Icon className="w-3.5 h-3.5" />
        <span className="max-w-[180px] truncate">{value}</span>
      </a>
      <CopyButton text={value} label={type === 'email' ? 'E-Mail' : 'Telefon'} />
    </div>
  );
};

const Section = ({ type, title, icon: Icon, data, query, setQuery, onRefresh, loading, onDelete }) => {
  const filtered = useMemo(() => {
    if (!query.trim()) return data;
    const q = query.toLowerCase();
    return data.filter((row) => JSON.stringify(row).toLowerCase().includes(q));
  }, [data, query]);

  const previewName = (row) => {
    if (type === 'seller-onboarding') return getPreview(row, ['seller_name', 'full_name', 'name', 'email']);
    if (type === 'onboarding') return getPreview(row, ['full_name', 'name', 'email']);
    return getPreview(row, ['name', 'email']);
  };

  const previewEmail = (row) => {
    if (type === 'seller-onboarding') return getPreview(row, ['seller_email', 'email']);
    return getPreview(row, ['email']);
  };

  const previewPhone = (row) => {
    if (type === 'seller-onboarding') return getPreview(row, ['seller_phone', 'phone']);
    return getPreview(row, ['phone']);
  };

  const previewCompany = (row) => getPreview(row, ['company_name', 'companyName']);
  
  const previewPrice = (row) => {
    const v = row?.asking_price;
    if (v === null || v === undefined || v === '') return '';
    return `${Number(v).toLocaleString('de-DE')} €`;
  };

  const previewMessage = (row) => {
    const msg = getPreview(row, ['message']);
    return msg.length > 60 ? `${msg.slice(0, 60)}…` : msg;
  };

  const previewSubject = (row) => getPreview(row, ['subject']);

  const handleDeleteClick = (e, row) => {
    e.stopPropagation();
    if (window.confirm(`"${previewName(row)}" wirklich löschen?`)) {
      onDelete(type, row.id);
    }
  };

  return (
    <Card className="border-2 border-gray-200 shadow-sm overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            {Icon && (
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Icon className="w-5 h-5 text-blue-600" />
              </div>
            )}
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              <p className="text-sm text-gray-500">{filtered.length} Einträge</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-full md:w-72">
              <Input 
                value={query} 
                onChange={(e) => setQuery(e.target.value)} 
                placeholder="Suchen…" 
                className="h-9"
              />
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onRefresh}
              disabled={loading}
              className="h-9"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Datum</th>
                <th className="text-left font-semibold px-4 py-3">Name</th>
                <th className="text-left font-semibold px-4 py-3">E-Mail</th>
                <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Telefon</th>
                {type === 'contact' ? (
                  <>
                    <th className="text-left font-semibold px-4 py-3">Betreff</th>
                    <th className="text-left font-semibold px-4 py-3">Nachricht</th>
                  </>
                ) : (
                  <th className="text-left font-semibold px-4 py-3">Unternehmen</th>
                )}
                {type === 'seller-onboarding' && (
                  <th className="text-right font-semibold px-4 py-3 whitespace-nowrap">Preis</th>
                )}
                <th className="text-center font-semibold px-4 py-3 whitespace-nowrap">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((row) => (
                <tr 
                  key={row.id} 
                  className="hover:bg-blue-50/50 transition-colors cursor-pointer"
                  onClick={() => window.location.href = `/admin/${type}/${row.id}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {isNew(row.created_at) && (
                        <Badge className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5">NEU</Badge>
                      )}
                      <span className="text-gray-600">{formatDateShort(row.created_at)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-900">{previewName(row)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <ContactCell type="email" value={previewEmail(row)} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <ContactCell type="phone" value={previewPhone(row)} />
                  </td>
                  {type === 'contact' ? (
                    <>
                      <td className="px-4 py-3 text-gray-700 max-w-[150px] truncate">
                        {previewSubject(row) || '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">
                        {previewMessage(row)}
                      </td>
                    </>
                  ) : (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-700">{previewCompany(row) || '-'}</span>
                      </div>
                    </td>
                  )}
                  {type === 'seller-onboarding' && (
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold text-green-700">{previewPrice(row)}</span>
                    </td>
                  )}
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-center gap-2">
                      <Link 
                        to={`/admin/${type}/${row.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button variant="outline" size="sm" className="h-8 px-3">
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Details
                        </Button>
                      </Link>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-8 px-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                        onClick={(e) => handleDeleteClick(e, row)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td 
                    className="px-4 py-10 text-center text-gray-500" 
                    colSpan={type === 'seller-onboarding' ? 7 : type === 'contact' ? 7 : 6}
                  >
                    Keine Einträge gefunden.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [onboarding, setOnboarding] = useState([]);
  const [seller, setSeller] = useState([]);
  const [contact, setContact] = useState([]);

  const [qOnboarding, setQOnboarding] = useState('');
  const [qSeller, setQSeller] = useState('');
  const [qContact, setQContact] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('adminToken') : null;

  const logout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const fetchAll = async () => {
    if (!token) {
      navigate('/admin/login');
      return;
    }

    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [a, b, c] = await Promise.all([
        axios.get(`${API_URL}/api/admin/onboarding`, { headers }),
        axios.get(`${API_URL}/api/admin/seller-onboarding`, { headers }),
        axios.get(`${API_URL}/api/admin/contact`, { headers }),
      ]);

      const onboardingData = Array.isArray(a.data) ? a.data : [];
      const sellerData = Array.isArray(b.data) ? b.data : [];
      const contactData = Array.isArray(c.data) ? c.data : [];

      if (!Array.isArray(a.data) || !Array.isArray(b.data) || !Array.isArray(c.data)) {
        setErrorMsg(
          'Die Daten konnten nicht korrekt geladen werden. Prüfe die Backend-Verbindung.'
        );
      } else {
        setErrorMsg('');
      }

      setOnboarding(onboardingData);
      setSeller(sellerData);
      setContact(contactData);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Zugriff nicht möglich',
        description: 'Bitte logge dich erneut ein.',
      });
      localStorage.removeItem('adminToken');
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, id) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = type === 'contact' ? 'contact' : type === 'onboarding' ? 'onboarding' : 'seller-onboarding';
      await axios.delete(`${API_URL}/api/admin/${endpoint}/${id}`, { headers });
      
      toast({
        title: 'Gelöscht',
        description: 'Der Eintrag wurde erfolgreich gelöscht.',
      });
      
      // Liste aktualisieren
      if (type === 'onboarding') {
        setOnboarding(prev => prev.filter(item => item.id !== id));
      } else if (type === 'seller-onboarding') {
        setSeller(prev => prev.filter(item => item.id !== id));
      } else if (type === 'contact') {
        setContact(prev => prev.filter(item => item.id !== id));
      }
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Fehler beim Löschen',
        description: err.response?.data?.detail || 'Der Eintrag konnte nicht gelöscht werden.',
      });
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stats
  const stats = useMemo(() => {
    const total = onboarding.length + seller.length + contact.length;
    const newToday = [...onboarding, ...seller, ...contact].filter(r => isNew(r.created_at)).length;
    return { total, newToday, onboarding: onboarding.length, seller: seller.length, contact: contact.length };
  }, [onboarding, seller, contact]);

  return (
    <div className="min-h-screen pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-sm text-gray-600">Alle Formulareingänge im Überblick</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link to="/admin/newsletter">
              <Button variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                <Mail className="w-4 h-4 mr-2" />
                Newsletter
              </Button>
            </Link>
            <Link to="/admin/listings">
              <Button className="bg-gray-900 hover:bg-gray-800 text-white">
                <Building2 className="w-4 h-4 mr-2" />
                Listings
              </Button>
            </Link>
            <Button variant="outline" onClick={logout}>
              Logout
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border border-gray-200">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-500">Gesamt</div>
            </CardContent>
          </Card>
          <Card className="border border-green-200 bg-green-50">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-green-700">{stats.newToday}</div>
              <div className="text-sm text-green-600">Neu (24h)</div>
            </CardContent>
          </Card>
          <Card className="border border-blue-200 bg-blue-50">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-blue-700">{stats.onboarding}</div>
              <div className="text-sm text-blue-600">Gründungen</div>
            </CardContent>
          </Card>
          <Card className="border border-orange-200 bg-orange-50">
            <CardContent className="pt-4 pb-4">
              <div className="text-2xl font-bold text-orange-700">{stats.seller}</div>
              <div className="text-sm text-orange-600">Verkäufe</div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <Card className="border-2 border-gray-200">
            <CardContent className="py-16 text-center">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Lade Daten…</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {errorMsg && (
              <Card className="border-2 border-orange-200 bg-orange-50">
                <CardContent className="py-4 text-sm text-orange-900">
                  <div className="font-semibold mb-1">Hinweis</div>
                  <div>{errorMsg}</div>
                </CardContent>
              </Card>
            )}
            
            <Section
              type="onboarding"
              title="Erfassungsformular (Gründung)"
              icon={FileText}
              data={onboarding}
              query={qOnboarding}
              setQuery={setQOnboarding}
              onRefresh={fetchAll}
              loading={loading}
              onDelete={handleDelete}
            />
            
            <Section
              type="seller-onboarding"
              title="Anfrageformular (Verkauf)"
              icon={Euro}
              data={seller}
              query={qSeller}
              setQuery={setQSeller}
              onRefresh={fetchAll}
              loading={loading}
              onDelete={handleDelete}
            />
            
            <Section
              type="contact"
              title="Kontaktanfragen"
              icon={MessageSquare}
              data={contact}
              query={qContact}
              setQuery={setQContact}
              onRefresh={fetchAll}
              loading={loading}
              onDelete={handleDelete}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
